import {
  Home,
  Building2,
  Hammer,
  RefreshCcw,
  Landmark,
  BadgePercent,
  Clock3,
  Percent,
  FileCheck2,
  Users,
  Wallet,
  Briefcase,
  User,
  IndianRupee,
  CalendarDays,
  IdCard,
  BadgeCheck,
  PhoneCall,
  ClipboardList,
  ShieldCheck,
  FileSearch,
  FileText,
} from "./icons";

import { EMPLOYMENT_TYPES } from "../../constants/employmentTypes";

import {
  process,
  whyMoneyMartItems,
  ctaFeatures,
  leadFormHighlights,
} from "./shared";

import {
  websiteSchema,
  organizationSchema,
  financialServiceSchema,
  localBusinessSchema,
  breadcrumbSchema,
} from "@/components/common/schema";

const schemas = [
  websiteSchema(),
  organizationSchema(),
  financialServiceSchema(),
  localBusinessSchema(),
  breadcrumbSchema([
    {
      name: "Home",
      path: "/",
    },
    {
      name: "Business Loan",
      path: "/business-loan",
    },
  ]),
];

export const seo = {
  title: "Business Loan | Money Mart",

  description:
    "Compare Business Loan offers from leading Banks & NBFCs with Money Mart. Get competitive interest rates, flexible repayment options, and expert guidance for business expansion, working capital, machinery purchase, and more.",

  keywords:
    "Business Loan, MSME Loan, Working Capital Loan, Term Loan, Business Finance, Money Mart",

  canonical: "/business-loan",

  structuredData: { schemas },
};

export const hero = {
  badge: "Trusted Business Loan Advisors",

  title: "Business Loans to Help Your Business Grow",

  description:
    "Whether you're expanding operations, purchasing equipment, managing working capital, or funding a new venture, Money Mart helps you compare Business Loan offers from leading Banks and NBFCs with expert guidance throughout the process.",

  highlights: [
    "Competitive Interest Rates",
    "Collateral & Unsecured Options",
    "Fast Loan Processing",
    "Dedicated Business Loan Experts",
  ],

  image: "/images/loans/business_loan.webp",

  primaryButtonText: "Get Free Consultation",

  secondaryButtonText: "Check Eligibility",
};

export const overview = {
  badge: "Business Loan Solutions",

  title: "Flexible Financing Solutions for Every Business",

  description:
    "Whether you're starting a new business, expanding operations, purchasing machinery, or managing working capital, we help you find the right Business Loan from India's leading lenders.",

  items: [
    {
      icon: Building2,
      title: "Business Expansion",
      description:
        "Fund business growth, open new branches, expand inventory, or scale operations with flexible financing.",
    },
    {
      icon: Briefcase,
      title: "Working Capital",
      description:
        "Maintain healthy cash flow and meet day-to-day operational expenses with ease.",
    },
    {
      icon: Landmark,
      title: "Machinery & Equipment",
      description:
        "Purchase machinery, equipment, vehicles, or commercial assets to grow your business.",
    },
    {
      icon: IndianRupee,
      title: "Business Growth",
      description:
        "Invest in marketing, technology, hiring, or infrastructure to accelerate business growth.",
    },
  ],
};

export const benefits = {
  badge: "Benefits",

  title: "Why Choose a Business Loan?",

  description:
    "Business Loans provide quick access to funds so you can grow your business without disrupting cash flow.",

  items: [
    {
      icon: BadgePercent,
      title: "Competitive Interest Rates",
      description:
        "Compare loan offers from multiple banks and NBFCs to secure attractive interest rates.",
    },
    {
      icon: RefreshCcw,
      title: "Flexible Repayment",
      description:
        "Choose repayment tenures that align with your business cash flow and financial goals.",
    },
    {
      icon: ShieldCheck,
      title: "Unsecured Options",
      description:
        "Unsecured Business Loans based on your eligibility.",
    },
    {
      icon: Clock3,
      title: "Quick Processing",
      description:
        "Enjoy faster approvals with streamlined documentation and expert assistance.",
    },
    {
      icon: FileText,
      title: "Minimal Documentation",
      description:
        "Simplified documentation requirements make the application process convenient.",
    },
    {
      icon: Users,
      title: "Expert Loan Assistance",
      description:
        "Our advisors help you compare lenders and choose the financing option best suited to your business.",
    },
  ],
};

export const types = {
  badge: "Business Loan Types",

  title: "Business Financing Solutions for Every Need",

  description:
    "Choose from a wide range of Business Loan products designed for startups, MSMEs, and established businesses.",

  items: [
    {
      icon: Briefcase,
      title: "Working Capital Loan",
      description:
        "Manage daily operational expenses and maintain healthy cash flow.",
    },
    {
      icon: Building2,
      title: "Business Expansion Loan",
      description:
        "Expand operations, open new locations, or increase production capacity.",
    },
    {
      icon: Landmark,
      title: "Term Loan",
      description:
        "Finance long-term business investments with structured repayment options.",
    },
    {
      icon: ClipboardList,
      title: "MSME Loan",
      description:
        "Funding solutions designed specifically for Micro, Small & Medium Enterprises.",
    },
    {
      icon: RefreshCcw,
      title: "Cash Credit / Overdraft",
      description:
        "Flexible credit facilities to manage short-term business cash flow requirements.",
    },
    {
      icon: IndianRupee,
      title: "Machinery Loan",
      description:
        "Purchase machinery, equipment, or commercial assets to support business growth.",
    },
  ],
};

export const eligibility = {
  badge: "Eligibility",

  title: "Check Your Business Loan Eligibility",

  description:
    "Eligibility varies across lenders. Our experts compare multiple lenders to maximize your approval chances and help you secure the most suitable Business Loan.",

  requirements: [
    {
      icon: User,
      title: "Age",
      value: "21 – 70 Years",
    },
    {
      icon: Briefcase,
      title: "Business Vintage",
      value: "Minimum 1–3 years (varies by lender)",
    },
    {
      icon: IndianRupee,
      title: "Business Turnover",
      value: "Stable revenue with adequate repayment capacity",
    },
    {
      icon: CalendarDays,
      title: "Credit Profile",
      value: "Healthy CIBIL score & banking history preferred",
    },
  ],

  approvalFactors: [
    "Business turnover and profitability",
    "Business vintage and operational stability",
    "Applicant's credit score and repayment history",
    "Banking transactions and cash flow",
    "Existing loans and financial liabilities",
    "Nature of business and lender policies",
  ],

  cta: {
    title: "Not sure if you're eligible?",

    description:
      "Our Business Loan experts will evaluate your profile, compare lenders, and recommend the best financing options with no obligation.",

    buttonText: "Check Eligibility Now",
  },
};

export const documents = {
  badge: "Documents Required",

  title: "Documents Required for a Business Loan",

  description:
    "The exact documents required depend on your business profile and loan purpose. Below is the standard document checklist used by Money Mart for most Business Loan applications.",

  groups: [
    {
      icon: Users,
      title: "Documents Required for All Applicants",

      documents: [
        "1 Selfie/Photograph (Applicant & Co-applicant)",
        "PAN Card (Applicant & Co-applicant)",
        "Latest Downloaded Aadhaar Card (Applicant & Co-applicant)",
      ],
    },

    {
      icon: Briefcase,
      title: "Additional Documents for Salaried Applicants",

      documents: [
        "Last 3 Months Salary Slips",
        "Last 2 Years Form 16",
        "Salary & Savings Account Statements for the Last 7 Months",
        "Existing Loan Details (Sanction Letter / Repayment Schedule), if applicable",
        "Complete Property Document Chain",
      ],
    },

    {
      icon: Building2,
      title: "Additional Documents for Self-Employed Applicants",

      documents: [
        "GST Registration Certificate",
        "UDYAM Registration Certificate",
        "Last 3 Years Income Tax Returns with Profit & Loss Statement & Balance Sheet",
        "Audit Report (if applicable)",
        "Current & Savings Account Statements for the Last 7 Months",
        "1 Year Banking Statements for Balance Transfer Cases",
        "Existing Loan Details, if applicable",
        "Complete Property Document Chain",
      ],
    },

    {
      icon: Home,
      title: "Additional Documents for Property Purchase",

      documents: [
        "Complete Chain of Property Documents",
        "Sale Agreement",
        "Seller's PAN Card",
        "Seller's Aadhaar Card",
        "Cancelled Cheque (Preferred) or Front Page of Passbook",
        "Seller's Contact Number",
      ],
    },

    {
      icon: Hammer,
      title: "Additional Documents for Self Construction",

      documents: [
        "Complete Chain of Property Documents",
        "Original Construction Estimate from a Registered Engineer",
        "Approved Building Map",
        "Construction Permission from the Concerned Authority",
      ],
    },

    {
      icon: RefreshCcw,
      title: "Additional Documents for Balance Transfer",

      documents: [
        "Complete Chain of Property Documents",
        "Copy of Existing Loan Sanction Letter",
        "Loan Statement (From Disbursement Till Date)",
        "List of Original Documents Held by Existing Lender",
        "Outstanding / Foreclosure Letter",
      ],
    },
  ],
};

export const loanProcess = {
  ...process,

  title: "Your Business Loan in 6 Simple Steps",

  description:
    "From your first enquiry to loan disbursement, our Business Loan specialists assist you throughout the process to ensure a smooth financing experience.",
};

export const whyMoneyMart = {
  badge: "Why Money Mart",

  title: "Why Apply Through Money Mart Instead of Visiting Multiple Banks?",

  description:
    "Compare Business Loan offers from multiple Banks and NBFCs through a single application. Our experienced advisors guide you through lender selection, documentation, and approval from start to finish.",

  items: whyMoneyMartItems,
};

export const faq = {
  badge: "Frequently Asked Questions",

  title: "Business Loan FAQs",

  description:
    "Find answers to some of the most common questions about Business Loans.",

  items: [
    {
      question: "Who can apply for a Business Loan?",

      answer:
        "Salaried individuals running proprietorships, self-employed professionals, MSMEs, partnerships, LLPs, and private limited companies can apply, subject to lender eligibility criteria.",
    },
    {
      question: "Is collateral mandatory for a Business Loan?",

      answer:
        "No. Many lenders offer unsecured Business Loans, while larger loan amounts may require collateral depending on the lender's policy.",
    },
    {
      question: "How much Business Loan can I get?",

      answer:
        "The eligible amount depends on your business turnover, profitability, banking history, repayment capacity, and lender policies.",
    },
    {
      question: "How long does approval take?",

      answer:
        "Most Business Loans are approved within a few working days after successful document verification, though timelines vary by lender.",
    },
    {
      question: "Can a new business apply?",

      answer:
        "Some lenders finance new businesses, but most prefer businesses with an established operating history of at least 1–3 years.",
    },
    {
      question: "What repayment tenure is available?",

      answer:
        "Repayment tenure generally ranges from 12 months to 84 months, depending on the lender and loan type.",
    },
  ],
};

export const cta = {
  badge: "Ready to Grow Your Business?",

  title: "Let's Find the Right Business Loan for Your Business",

  description:
    "Compare Business Loan offers from leading Banks and NBFCs, receive expert guidance, and secure financing that supports your business goals.",

  features: ctaFeatures,

  primaryButton: {
    text: "Apply for Business Loan",
  },

  secondaryButton: {
    text: "Call +91 92144 17434",
    href: "tel:+919214417434",
  },
};

export const leadForm = {
  badge: "Free Business Loan Consultation",

  title: "Speak to a Business Loan Expert",

  description:
    "Looking for the right Business Loan? Our advisors compare offers from multiple lenders, help maximize your eligibility, and guide you through every stage of the application process.",

  highlights: leadFormHighlights,

  phone: {
    label: "Call us directly",
    text: "+91 92144 17434",
    href: "tel:+919214417434",
  },

  form: {
    title: "Get Free Consultation",

    description:
      "Fill in your details and our Business Loan expert will contact you shortly.",

    placeholders: {
      name: "Full Name *",
      mobile: "Mobile Number *",
      employment: "Business Type",
      income: "Annual Turnover",
      loanAmount: "Required Loan Amount",
    },

    incomeField: "annualTurnover",

    employmentOptions: EMPLOYMENT_TYPES,

    incomeOptions: [
      {
        value: "below-10l",
        label: "Below ₹10 Lakhs",
      },
      {
        value: "10-25l",
        label: "₹10 Lakhs – ₹25 Lakhs",
      },
      {
        value: "25-50l",
        label: "₹25 Lakhs – ₹50 Lakhs",
      },
      {
        value: "50l-1cr",
        label: "₹50 Lakhs – ₹1 Crore",
      },
      {
        value: "above-1cr",
        label: "Above ₹1 Crore",
      },
    ],

    submitButton: "Get Free Consultation",

    privacyText:
      "Your information is secure and will only be used to assist with your Business Loan enquiry. No spam. No hidden charges.",
  },
};

const businessLoan = {
  seo,
  hero,
  overview,
  benefits,
  types,
  eligibility,
  documents,
  process: loanProcess,
  whyMoneyMart,
  faq,
  cta,
  leadForm,
};

export default businessLoan;
